import React from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Heading2,
  Image,
  ModalBody,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Overflow,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { getProductImage } from '@features/scrollable-basket'
import type { BasketUpsellProduct } from '@library/api-menu-experience'

import { ProductButtons } from 'components/Product/Buttons'
import { sanitize } from 'utils/sanitizer'

type LimitReached =
  | false
  | {
      type: 'box' | 'item' | 'category'
      value: string | number
    }

type BasketProductDetailProps = {
  isAvailable: boolean
  limitReached: LimitReached
  onAdd: (productId: string) => void
  onClose: () => void
  onRemove: (productId: string) => void
  outOfStock: boolean
  product: BasketUpsellProduct
  qty: number
}

const ProductTitle = withCitrusTheme(Heading2, (theme) => ({
  ...(theme.responsiveFont([6, null, 7, 10]) as object),
  maxWidth: '90%',
}))

const ProductImage = withCitrusTheme(Image, () => ({
  height: '100%',
  objectFit: 'cover',
  width: '100%',
}))

const ProductImageFrame = withCitrusTheme(Box, (theme) => ({
  aspectRatio: '3 / 2',
  backgroundColor: theme.color(Color.ColdGrey_100),
  overflow: 'hidden',
  width: '100%',
}))

const FooterContent = withCitrusTheme(Box, () => ({
  overflowX: Overflow.Visible,
  overflowY: Overflow.Visible,
  position: 'relative',
  width: '100%',
}))

export const BasketProductDetail = ({
  isAvailable,
  limitReached,
  onAdd,
  onClose,
  onRemove,
  outOfStock,
  product,
  qty,
}: BasketProductDetailProps) => {
  const image = getProductImage(product)

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <ProductTitle data-testing="basketProductDetailTitle">{product.title}</ProductTitle>
      </ModalHeader>
      <ModalBody paddingH={0} paddingV={0}>
        <Box
          data-testid="basket-product-detail"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={5}
          paddingBottom={4}
        >
          {image.src && (
            <ProductImageFrame>
              <ProductImage
                alt={image.alt}
                sizes="(min-width: 768px) 640px, 100vw"
                src={image.src}
                srcSet={image.srcSet}
              />
            </ProductImageFrame>
          )}
          {product.descriptionHTML && (
            <Box paddingH={[4, 6]}>
              <Box dangerouslySetInnerHTML={{ __html: sanitize(product.descriptionHTML) }} />
            </Box>
          )}
          {product.attributesHTML && (
            <Box paddingH={[4, 6]}>
              <Box dangerouslySetInnerHTML={{ __html: sanitize(product.attributesHTML) }} />
            </Box>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <FooterContent>
          <Box paddingBottom={2}>
            <Text color={Color.ColdGrey_900} fontFamily={FontFamily.Bold} size={3}>
              {product.listPrice}
            </Text>
          </Box>
          <ProductButtons
            countLabel={(count) => `${count} added`}
            isAvailable={isAvailable}
            limitReached={limitReached}
            onAdd={onAdd}
            onRemove={onRemove}
            outOfStock={outOfStock}
            productId={product.id}
            qty={qty}
            showPopUp
          />
        </FooterContent>
      </ModalFooter>
    </>
  )
}
